import React, {ReactNode} from 'react'
import SVGCheck from '../../../../../assets/icons/check-small.svg'
import SVGClear from '../../../../../assets/icons/clear-small.svg'
import styled, {css} from 'styled-components'
import {fontSmall} from '../css'

const HandleInner = styled.div<{disabled?: boolean}>`
  width: 16px;
  height: 16px;
  border-radius: 999px;
  background: ${({theme}) => theme.white};
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: ${({theme, disabled}) =>
    disabled ? theme.colors.grey10 : theme.colors.blue11};
  box-shadow: 0px 0.8px 4px 0px rgba(0, 0, 0, 0.2);
`
const IconAligner = styled.div<{checked: boolean}>`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  transform-origin: center;
  transform: ${({checked}) => (checked ? 'rotate(0deg)' : 'rotate(-90deg)')};
  svg {
    position: absolute;
    transition: all 0.2s ease;
  }
  .check {
    opacity: ${({checked}) => (checked ? '1' : '0')};
  }
  .clear {
    opacity: ${({checked}) => (checked ? '0' : '1')};
  }
`

const Handle = ({
  checked,
  disabled
}: {
  checked: boolean
  disabled?: boolean
}) => (
  <HandleInner disabled={disabled}>
    <IconAligner checked={checked}>
      <SVGCheck className="check" />
      <SVGClear className="clear" />
    </IconAligner>
  </HandleInner>
)

export interface SwitchProps {
  onChange?: (checked: boolean) => void
  checked: boolean
  tabIndex?: number
  id?: string
  style?: React.CSSProperties
  disabled?: boolean
  readOnly?: boolean
  label?: ReactNode
}

const SwitchInner = styled.div<{
  checked: boolean
  disabled?: boolean
}>`
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  width: 36px;
  height: 20px;
  padding: 2px;
  padding-left: ${({checked}) => (checked ? '18px' : '2px')};
  background: ${({checked, theme, disabled}) =>
    disabled
      ? checked
        ? theme.colors.grey10
        : theme.colors.grey3
      : checked
      ? theme.colors.blue11
      : theme.colors.grey3};
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
`

const SwitchContainer = styled.label<{
  checked: boolean
  disabled?: boolean
}>`
  position: relative;
  display: inline-flex;
  justify-content: space-between;
  align-items: center;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
  ${({disabled, theme, checked}) =>
    !disabled &&
    css`
      &:hover,
      &:focus-within {
        ${SwitchInner} {
          background: ${checked ? theme.colors.blue12 : theme.colors.grey4};
        }
        ${HandleInner} {
          box-shadow: ${theme.shadowDepth2};
        }
      }
      &:active {
        ${SwitchInner} {
          background: ${theme.colors.blue13};
          background: ${checked ? theme.colors.blue13 : theme.colors.grey6};
        }
        ${HandleInner} {
          box-shadow: ${theme.shadowDepth1};
        }
      }
    `}
`

const InputOverlay = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;
  width: 0;
  height: 0;
`

const SwitchControl = ({
  onChange,
  checked,
  tabIndex,
  id,
  ...props
}: SwitchProps) => {
  const disabled = props.readOnly || props.disabled || !onChange
  return (
    <SwitchContainer checked={checked} disabled={disabled} {...props}>
      <SwitchInner checked={checked} disabled={disabled}>
        <Handle checked={checked} disabled={disabled} />
        <InputOverlay
          tabIndex={tabIndex}
          type="checkbox"
          id={id}
          checked={checked}
          disabled={disabled}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            onChange?.(e.target.checked)
          }
        />
      </SwitchInner>
    </SwitchContainer>
  )
}

const Label = styled.label`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: space-between;
  ${fontSmall}
`

export const Switch = ({label, ...props}: SwitchProps) => {
  return label ? (
    <Label>
      {label} <SwitchControl {...props} />
    </Label>
  ) : (
    <SwitchControl {...props} />
  )
}
